/**
 * Visão mensal do pipeline: o que entrou, fechou, perdeu e ficou parado em cada mês.
 *
 * Cada evento conta no mês em que ACONTECEU, não no mês em que o lead nasceu:
 *   entrada  → created_at
 *   ganho    → closed_at (lead em Fechado)
 *   perda    → lost_at   (lead em Perdido ou Repescagem, mesma regra de CONTA_COMO_PERDA)
 * Leads antigos, de antes de closed_at/lost_at existirem, caem no stage_changed_at.
 *
 * Mês é 'AAAA-MM' no fuso do navegador: uma venda fechada às 22h do dia 31 em
 * Brasília é do mês 31, mesmo já sendo dia 1º em UTC.
 */
import { CONTA_COMO_PERDA, FORA_DO_PIPELINE } from './pipeline';
import { calcContractTotal } from './formato';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

/** 'AAAA-MM' de um ISO com hora (fuso local) ou de uma data pura 'AAAA-MM-DD'. */
function mesDe(valor) {
  if (!valor) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor.slice(0, 7);
  const d = new Date(valor);
  if (isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function mesAtual(agora = new Date()) { return mesDe(agora.toISOString()); }

function somarMes(mes, n) {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(a, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function rotuloMes(mes, curto = false) {
  const [a, m] = mes.split('-').map(Number);
  const nome = MESES[m - 1];
  return curto ? `${nome.slice(0, 3)}/${String(a).slice(2)}` : `${nome} de ${a}`;
}

const dataGanho = (l) => l.closed_at || l.stage_changed_at || l.updated_at;
const dataPerda = (l) => l.lost_at || l.stage_changed_at || l.updated_at;
const ganho  = (l) => l.stage === 'fechado';
const perda  = (l) => CONTA_COMO_PERDA.includes(l.stage);
const valor  = (l) => Number(l.value) || 0;
const contrato = (l) => calcContractTotal(l.value, l.recurrence);
const soma = (lista, f) => lista.reduce((s, l) => s + f(l), 0);

/** Mês em que o lead saiu do pipeline (ganho ou perda), ou null se segue aberto. */
function mesDeSaida(l) {
  if (ganho(l)) return mesDe(dataGanho(l));
  if (perda(l)) return mesDe(dataPerda(l));
  return null;
}

/**
 * Estava aberto no fim do mês: já existia e ainda não tinha saído.
 * O valor e a etapa usados são os de HOJE; o app não guarda foto do passado.
 */
function abertoNoFimDo(l, mes) {
  const nasceu = mesDe(l.created_at);
  if (!nasceu || nasceu > mes) return false;
  const saiu = mesDeSaida(l);
  return !saiu || saiu > mes;
}

function resumoDoMes(leads, mes, agora = new Date()) {
  const atual    = mesAtual(agora);
  const entraram = leads.filter(l => mesDe(l.created_at) === mes);
  const ganhos   = leads.filter(l => ganho(l) && mesDe(dataGanho(l)) === mes);
  const perdidos = leads.filter(l => perda(l) && mesDe(dataPerda(l)) === mes);
  const abertos  = leads.filter(l => abertoNoFimDo(l, mes));

  const decididos = ganhos.length + perdidos.length;
  const ciclos = ganhos
    .map(l => (new Date(dataGanho(l)) - new Date(l.created_at)) / 86400000)
    .filter(d => isFinite(d) && d >= 0);

  // Previsão: só faz sentido para o mês corrente ou futuro. Soma o que já fechou com o
  // pipe aberto que tem previsão de fechamento naquele mês, ponderado pela probabilidade
  // (50% quando ninguém informou, a mesma regra do pipeline ponderado dos KPIs).
  const comPrevisao = mes >= atual
    ? leads.filter(l => !FORA_DO_PIPELINE.includes(l.stage) && mesDe(l.forecast_date) === mes)
    : [];
  const prob = (l) => (l.forecast_prob === null || l.forecast_prob === undefined || l.forecast_prob === '' ? 50 : Number(l.forecast_prob)) / 100;

  const motivos = {};
  for (const l of perdidos) { const k = l.loss_reason || 'sem_motivo'; motivos[k] = (motivos[k] || 0) + 1; }
  const origens = {};
  for (const l of entraram) { const k = l.origem || 'sem_origem'; origens[k] = (origens[k] || 0) + 1; }

  const porEtapa = {};
  for (const l of abertos) {
    const k = l.stage || 'backlog';
    porEtapa[k] = porEtapa[k] || { count: 0, value: 0 };
    porEtapa[k].count++; porEtapa[k].value += valor(l);
  }

  return {
    mes, atual: mes === atual, passado: mes < atual,
    entraram, ganhos, perdidos, abertos, comPrevisao,
    ganho_valor:     soma(ganhos, valor),
    ganho_contrato:  soma(ganhos, contrato),
    perdido_valor:   soma(perdidos, valor),
    aberto_valor:    soma(abertos, valor),
    conversao:       decididos ? ganhos.length / decididos * 100 : null,
    ticket_medio:    ganhos.length ? soma(ganhos, contrato) / ganhos.length : null,
    ciclo_medio:     ciclos.length ? ciclos.reduce((s, d) => s + d, 0) / ciclos.length : null,
    previsto_pipe:   soma(comPrevisao, l => valor(l) * prob(l)),
    previsto_total:  soma(ganhos, valor) + soma(comPrevisao, l => valor(l) * prob(l)),
    motivos, origens, porEtapa,
  };
}

/** Do mês do lead mais antigo até o mês atual, do mais novo para o mais antigo. */
function mesesDisponiveis(leads, agora = new Date()) {
  const atual = mesAtual(agora);
  const datas = leads.map(l => mesDe(l.created_at)).filter(Boolean).sort();
  let m = datas[0] && datas[0] < atual ? datas[0] : atual;
  const lista = [];
  while (m <= atual) { lista.unshift(m); m = somarMes(m, 1); }
  return lista;
}

/** Linha por mês para a tabela mês a mês (mais recente primeiro). */
function serieMensal(leads, n = 12, agora = new Date()) {
  return mesesDisponiveis(leads, agora).slice(0, n).map(mes => {
    const r = resumoDoMes(leads, mes, agora);
    return {
      mes, entraram: r.entraram.length,
      ganhos: r.ganhos.length, ganho_valor: r.ganho_valor,
      perdidos: r.perdidos.length, perdido_valor: r.perdido_valor,
      abertos: r.abertos.length, aberto_valor: r.aberto_valor,
      conversao: r.conversao,
    };
  });
}

export { mesAtual, mesDe, mesesDisponiveis, resumoDoMes, rotuloMes, serieMensal, somarMes };
